import { useState, useRef, useEffect, useTransition, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { DateSelectArg, EventDropArg } from '@fullcalendar/core';
import { useAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { activeTabAtom } from '../../atoms/v0/uiAtoms';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import { format, parseISO, differenceInSeconds, isWithinInterval, startOfDay, endOfDay, subHours } from 'date-fns';
import { Task, CreateTaskData, UpdateTaskData } from '../../types/task';
import { Project } from '../../types/project'; // Import Project type
import {
  fetchTasksAtom,
  updateTaskAtom,
  deleteTaskAtom,
  createTaskAtom,
} from '../../atoms/v0/taskAtoms';
import { translatedTasksAtom } from '../../atoms/v0/translationAtoms';
import { fetchProjectsAtom } from '../../atoms/v0/projectAtoms';
import {
  // Container, // Remove Container import
  useBreakpointValue,
  Spinner,
  Button,
  useToast,
  Text,
  useColorModeValue,
  Checkbox,
  useDisclosure,
  Box,
  HStack,
} from '@chakra-ui/react';
import { FiRefreshCw, FiFolder } from 'react-icons/fi';
import { TaskFormModal } from '../../components/v0/tasks/TaskFormModal';
import { useTranslation } from 'react-i18next';
import { EventList } from '../../components/v0/calendar/EventList';
import { DiaryEditor } from '../../components/v0/calendar/DiaryEditor';
import { ViewToggleButtons } from '../../components/v0/calendar/ViewToggleButtons';
import { TimezoneSettingsModal } from '../../components/v0/calendar/TimezoneSettingsModal';
import { taskToEvents, CalendarEvent } from '../../utils/calendarUtils';
import { baseCalendarStyles } from '../../styles/v0/calendarStyles';
import { FiChevronLeft, FiChevronRight, FiChevronUp, FiChevronDown, FiSettings } from 'react-icons/fi'; // Import icons
import { toZonedTime, format as formatTz } from 'date-fns-tz'; // Import timezone functions - Use toZonedTime
import { addAdditionalTimezoneAxes } from '../../utils/calendarDomUtils'; // Import the DOM utility
import { projectByIdAtom } from '../../atoms/v0/projectAtoms'; // Import project atom
import { useAuth } from '../../lib/auth/AuthProvider'; // Import useAuth
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { H as Highlight } from 'highlight.run'; // Import Highlight

// Define CalendarView type (can be moved to a shared types file later)
type CalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

// Define props interface for Calendar component
interface CalendarProps {
  calendarView: CalendarView;
  setCalendarView: React.Dispatch<React.SetStateAction<CalendarView>>;
  calendarRef: React.MutableRefObject<FullCalendar | null>;
}

// Use loadable to wrap the TRANSLATED tasks atom
const loadableTranslatedTasksAtom = loadable(translatedTasksAtom);

// Modify Calendar component to accept props
export const Calendar: React.FC<CalendarProps> = ({
  calendarView,
  setCalendarView,
  calendarRef
}) => {
  const { user, loading: authLoading } = useAuth(); // Use auth hook
  const maxW = useBreakpointValue({ base: '100%', md: '90%', lg: '1200px' });
  const { isDarkMode } = useThemeMode();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [tasksLoadable] = useAtom(loadableTranslatedTasksAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  const [, updateTask] = useAtom(updateTaskAtom);
  const [, deleteTask] = useAtom(deleteTaskAtom);
  const [, createTask] = useAtom(createTaskAtom);
  const [, fetchProjects] = useAtom(fetchProjectsAtom);
  const toast = useToast();
  const moreLinkBg = useColorModeValue('gray.100', 'gray.700');
  const moreLinkColor = useColorModeValue('gray.600', 'gray.300');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTaskInitialData, setNewTaskInitialData] = useState<Partial<CreateTaskData>>({});
  const { t, i18n } = useTranslation(['v0/calendar', 'v0/common']);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false); // State for panel collapse
  const isDesktop = useBreakpointValue({ base: false, md: true }); // Check if desktop
  const highlightedElementRef = useRef<HTMLElement | null>(null); // Ref for selected day highlight
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null); // Ref for the calendar wrapper div
  const getProjectById = useAtom(projectByIdAtom)[0]; // Get the getter function for projects - MOVE THIS OUTSIDE RENDER
  const [isPending, startTransition] = useTransition(); // Add transition state

  // State for additional timezones - with localStorage persistence
  const [additionalTimezones, setAdditionalTimezones] = useState<string[]>(() => {
    try {
      const savedTimezones = localStorage.getItem('tempo_additional_timezones');
      return savedTimezones ? JSON.parse(savedTimezones) : ['Asia/Tokyo', 'Europe/London']; // Default if nothing saved
    } catch (error) {
      console.error("Error reading timezones from localStorage:", error);
      return ['Asia/Tokyo', 'Europe/London']; // Fallback to default
    }
  });
  const { isOpen: isTimezoneModalOpen, onOpen: onTimezoneModalOpen, onClose: onTimezoneModalClose } = useDisclosure(); // Modal state

  // --- Color mode values for Tailwind ---
  const bgColor = useColorModeValue('white', 'gray.800'); // Used for Box replacement
  const eventTimeColor = useColorModeValue('gray.600', 'gray.400'); // Color for event time text
  const borderColor = useColorModeValue('gray.100', 'gray.700'); // Used for Box replacement
  const moreLinkBgClass = useColorModeValue('bg-gray-100', 'bg-gray-700'); // Tailwind class
  const moreLinkColorClass = useColorModeValue('text-gray-600', 'text-gray-300'); // Tailwind class
  const loadingTextColorClass = useColorModeValue('text-gray-500', 'text-gray-400');
  const errorTextColorClass = useColorModeValue('text-red-500', 'text-red-400');

  // Initialize tasks and projects - WAIT FOR AUTH
  useEffect(() => {
    console.log('[v0/Calendar.tsx] Component Mounted');
    try {
      console.log('[v0/Calendar.tsx] Attempting to stop Highlight.run recording...');
      Highlight.stop();
      console.log('[v0/Calendar.tsx] Highlight.run H.stop() called.');
    } catch (error) {
      console.error('[v0/Calendar.tsx] Error calling Highlight.stop():', error);
    }

    if (!authLoading && user) {
      console.log('[v0/Calendar.tsx] Auth loaded, fetching initial data...');
      fetchTasks();
      fetchProjects();
    } else if (!authLoading && !user) {
      console.log('[Calendar v0] Auth loaded but no user, skipping initial fetch.');
      // Optionally clear existing data if needed when user logs out
    } else {
      console.log('[Calendar v0] Auth still loading, waiting to fetch initial data...');
    }
    // Dependencies now include auth state
  }, [authLoading, user, fetchTasks, fetchProjects]);

  // Convert tasks to events - Memoized
  const events: CalendarEvent[] = useMemo(() => {
    if (tasksLoadable.state === 'hasData') {
        // Ensure tasksLoadable.data is accessed only after checking state
        return tasksLoadable.data.flatMap(task => taskToEvents(task));
    }
    return [];
  // Dependency should be tasksLoadable itself, or more specific parts if stable.
  // If tasksLoadable.data was a direct dependency, it could cause issues if not guarded by state check.
  }, [tasksLoadable]);

  // Filter events for the selected date - Memoized
  const selectedDateEvents = useMemo(() => events.filter(event => {
    if (!event.start || !event.end) return false;
    const selectedDayStart = startOfDay(selectedDate);
    const selectedDayEnd = endOfDay(selectedDate);
    return isWithinInterval(selectedDayStart, { start: event.start, end: event.end }) ||
           isWithinInterval(selectedDayEnd, { start: event.start, end: event.end }) ||
           isWithinInterval(event.start, { start: selectedDayStart, end: selectedDayEnd }) ||
           isWithinInterval(event.end, { start: selectedDayStart, end: selectedDayEnd });
  }), [events, selectedDate]);

  // --- Callback Memoization for event handlers ---
  const handleToggleDone = useCallback(async (task: Task, done: boolean) => {
    const success = await updateTask({ id: task.id, done });
    if (!success) {
      toast({ title: t('v0/calendar:notifications.updateFailed'), status: 'error', duration: 3000, isClosable: true });
    }
  }, [updateTask, toast, t]);

  const handleDeleteTask = useCallback(async (id: string) => {
    const success = await deleteTask(id);
    if (!success) {
      toast({ title: t('v0/calendar:notifications.deleteFailed'), status: 'error', duration: 3000, isClosable: true });
    } else {
      if (selectedTask?.id === id) {
         setIsTaskFormOpen(false);
         setSelectedTask(null);
      }
    }
  }, [deleteTask, toast, t, selectedTask?.id, setIsTaskFormOpen, setSelectedTask]);

  const handleSelectTask = useCallback((task: Task) => {
    setSelectedTask(task);
    setNewTaskInitialData({});
    setIsTaskFormOpen(true);
  }, [setSelectedTask, setNewTaskInitialData, setIsTaskFormOpen]);

  const handleTaskSubmit = useCallback(async (data: CreateTaskData | UpdateTaskData) => {
    setIsSubmitting(true);
    let success = false;
    try {
       if ('id' in data && data.id) {
          success = await updateTask(data as UpdateTaskData);
          if (!success) {
             toast({
                title: t('v0/calendar:notifications.updateTaskFailed'),
                status: 'error',
                duration: 3000,
                isClosable: true,
             });
          } else {
             toast({
               title: t('v0/calendar:notifications.updateTaskSuccess'),
               status: 'success',
               duration: 2000,
               isClosable: true,
             });
             setIsTaskFormOpen(false); // Close modal on successful update
             setSelectedTask(null);
          }
       } else {
         // 处理创建任务逻辑
         success = await createTask(data as CreateTaskData);
         if (!success) {
           toast({
             title: t('v0/calendar:notifications.createTaskFailed'),
             status: 'error',
             duration: 3000,
             isClosable: true,
           });
         } else {
           toast({
             title: t('v0/calendar:notifications.createTaskSuccess'),
             status: 'success',
             duration: 2000,
             isClosable: true,
           });
           setIsTaskFormOpen(false); // 成功创建后关闭模态框
           setNewTaskInitialData({}); // 清空初始数据
         }
       }

    } catch (error) {
       console.error("Error submitting task:", error);
       toast({
         title: t('v0/common:errors.general'),
         status: 'error',
         duration: 3000,
         isClosable: true,
       });
    } finally {
      setIsSubmitting(false);
      // Don't close modal here unconditionally, only on success
    }
  }, [updateTask, createTask, toast, t, setIsTaskFormOpen, setSelectedTask, setNewTaskInitialData, setIsSubmitting]);

  const handleEventDrop = useCallback(async (dropInfo: EventDropArg) => {
    const { event } = dropInfo;
    const task = event.extendedProps?.task as Task | undefined;

    if (!task) {
      console.warn("Dropped event without associated task data:", event);
      dropInfo.revert(); // Revert the change in the calendar UI
      return;
    }

    const newStartAt = event.start ? event.start.toISOString() : undefined;
    // Calculate cost based on new start and end times if end time exists
    let newCost: number | undefined = undefined;
    if (event.start && event.end) {
      newCost = differenceInSeconds(event.end, event.start);
      if (newCost <= 0) { // Ensure cost is positive, otherwise revert
         console.warn("Dragged event resulted in zero or negative duration.");
         // dropInfo.revert(); // Revert maybe too strict? Allow setting duration later?
         // Set cost to undefined if duration is invalid, user needs to fix it in modal.
         newCost = undefined;
      }
    } else {
       // If end time is somehow null after drop (shouldn't happen with default drag), keep original cost?
       newCost = task.cost || undefined; // Or set to undefined?
    }

    // Prepare data for update
    const updateData: UpdateTaskData = {
      id: task.id,
      startAt: newStartAt,
      cost: newCost, // Update cost based on new duration
      // Note: We are not updating dueAt here, drag only affects start/duration
    };

    setIsSubmitting(true); // Indicate background activity
    const success = await updateTask(updateData);
    setIsSubmitting(false);

    if (!success) {
      toast({
        title: t('v0/calendar:notifications.updateFailed'),
        description: t('v0/calendar:errors.dragDropFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      dropInfo.revert(); // Revert the change on failure
    } else {
       toast({ // Optional success toast
         title: t('v0/calendar:notifications.updateSuccess'),
         status: 'success',
         duration: 1500,
         isClosable: true,
       });
    }
  }, [updateTask, toast, t, setIsSubmitting, differenceInSeconds]);

  const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
    console.log('[Calendar] handleDateSelect triggered:', selectInfo);
    const startDate = selectInfo.start;
    const endDate = selectInfo.end;

    // 计算选择的时间范围（秒）
    const durationInSeconds = differenceInSeconds(endDate, startDate);

    // 准备创建任务的初始数据
    const initialData: Partial<CreateTaskData> = {
      title: '', // 空标题，需要用户填写
      startAt: startDate.toISOString(),
      cost: durationInSeconds > 0 ? durationInSeconds : undefined,
      // 如果是点击单个日期/时间单元格而非拖拽，则将dueAt设置为相同时间
      dueAt: durationInSeconds === 0 ? startDate.toISOString() : undefined
    };

    // Wrap state updates in startTransition
    startTransition(() => {
      setSelectedTask(null); // 确保不是编辑模式
      setNewTaskInitialData(initialData);
      setIsTaskFormOpen(true);
    });

    // 必须取消选择，否则会影响后续操作
    if (calendarRef.current) {
      calendarRef.current.getApi().unselect();
    }
  }, [calendarRef, setSelectedTask, setNewTaskInitialData, setIsTaskFormOpen, startTransition, differenceInSeconds]);

  const handleDateClick = useCallback((clickInfo: DateClickArg) => {
    // console.log('[Calendar] handleDateClick triggered for date:', clickInfo.date);
    setSelectedDate(clickInfo.date);
  }, [setSelectedDate]);

  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date; view: any }) => {
    // setActiveView(dateInfo.view.type as CalendarView); // No longer needed
    // Ensure selectedDate reflects the *start* of the viewed range in these views
    if (dateInfo.view.type === 'timeGridDay' || dateInfo.view.type === 'timeGridWeek' || dateInfo.view.type === 'listWeek') {
      setSelectedDate(dateInfo.start);
    }

    // --- Scroll to appropriate time in timeGrid views ---
    if (dateInfo.view.type.startsWith('timeGrid')) {
      // Scroll to 2 hours before the current time to position now indicator lower
      const now = new Date();
      const scrollTargetDate = subHours(now, 2);
      const targetScrollTime = scrollTargetDate.toTimeString().slice(0, 8); // Format as HH:MM:SS
      console.log(`[Calendar] handleDatesSet: Calculated targetScrollTime: ${targetScrollTime}`);

      // Use setTimeout to ensure the call happens after render potentially completes
      setTimeout(() => {
        if (calendarRef.current) {
          try {
            calendarRef.current.getApi().scrollToTime(targetScrollTime);
            console.log(`[Calendar] Attempted scroll to time: ${targetScrollTime}`);
          } catch (error) {
            console.error("[Calendar] Error scrolling to time:", error);
          }
        }
      }, 100); // Increased delay to 100ms
    }
    // --- End Scroll ---

    // --- Add/Update additional timezone axes after view/date changes ---
    // Only run for timeGrid views
    if (dateInfo.view.type.startsWith('timeGrid')) {
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) return; // Exit if API is not available

      // Delay slightly to ensure DOM is likely updated
      const timeoutId = setTimeout(() => {
        addAdditionalTimezoneAxes(
          calendarRef,
          calendarWrapperRef,
          additionalTimezones,
          calendarApi.getDate() ?? null // Pass the current view's date or null
        );
        console.log('[Calendar] Updated additional timezone axes.');
      }, 150);

      // We need a way to clear this timeout if the component unmounts or effect re-runs quickly
      // This might be complex here, consider if a simple delay is sufficient for now.
    }
    // --- End Add/Update Axes ---

    // Removed manual header listener logic
  }, [setSelectedDate, calendarRef, calendarWrapperRef, additionalTimezones, addAdditionalTimezoneAxes, subHours]);

  // --- Highlight selected date ---
  const updateHighlight = useCallback((newDate: Date) => {
    // Remove highlight from the previously selected element
    if (highlightedElementRef.current) {
      highlightedElementRef.current.classList.remove('selected-day-highlight');
    }

    // Format the new date to YYYY-MM-DD for the selector
    const formattedDate = format(newDate, 'yyyy-MM-dd');

    // Find the new element to highlight
    // FullCalendar uses data-date attribute on the <td> element in dayGrid and timeGrid views
    const newElement = document.querySelector(`td.fc-day[data-date="${formattedDate}"]`) as HTMLElement;

    if (newElement) {
      newElement.classList.add('selected-day-highlight');
      highlightedElementRef.current = newElement;
    } else {
      // Date might not be visible in the current view
      highlightedElementRef.current = null;
    }
  }, [format]);

  // Apply highlight whenever selectedDate changes
  useEffect(() => {
    // Use setTimeout to ensure the DOM update after potential re-renders
    setTimeout(() => updateHighlight(selectedDate), 0);
  }, [selectedDate, updateHighlight]);

  // --- End Highlight ---

  // --- Custom Event Rendering ---
  const renderEventContent = useCallback((eventInfo: any, getProjectByIdFn: (id: string | null) => Project | null, timeColor: string) => {
    const task = eventInfo.event.extendedProps.task as Task | undefined;

    if (!task) {
      // Handle cases where task might not be available (e.g., external events?)
      return <i>{eventInfo.event.title}</i>; // Default italic title
    }

    // Don't show checkbox for the separate "Due: ..." events
    const isDueMarkerEvent = eventInfo.event.id.endsWith('-due');

    return (
      <Box
        display="flex"
        alignItems="flex-start" // Align checkbox to the top of the text
        w="100%"
        h="100%"
        cursor="pointer" // Indicate clickability
        overflow="hidden"
        px="2px" // Minimal horizontal padding inside the event
        className="custom-event-content" // Add class for potential CSS targeting
        onClick={() => handleSelectTask(task)} // Trigger edit modal on click
      >
        {!isDueMarkerEvent && (
          <Checkbox
            size="sm"
            isChecked={task.done}
            onChange={(e) => handleToggleDone(task, e.target.checked)}
            aria-label={t('v0/tasks:markAsDone')}
            colorScheme="pink" // Or another color scheme
            mt="1px" // Fine-tune vertical alignment
            mr={1.5} // Space between checkbox and text
            // Prevent click on checkbox from selecting the event for editing
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Box
          flex="1"
          ml={!isDueMarkerEvent ? 0 : 1.5} // Add left margin if checkbox is hidden
          overflow="hidden"
          // Allow text wrapping
          whiteSpace="normal" // Changed from nowrap
          wordBreak="break-word" // Allow breaking long words if necessary
          // Apply line-through style directly if done
          textDecoration={task.done ? 'line-through' : 'none'}
          opacity={task.done ? 0.7 : 1} // Apply opacity here instead of class
          className="event-title-text" // Add class for potential CSS targeting
        >
          {/* Remove "Due: " prefix if it's a due marker event, title already includes it */}
          {isDueMarkerEvent ? task.title : eventInfo.event.title}
          {/* Show time for non-all-day events in timegrid view */}
          {eventInfo.view.type.startsWith('timeGrid') && !eventInfo.event.allDay && (
              <Text fontSize="xs" color={timeColor} mt={0.5} noOfLines={1}>
                {format(eventInfo.event.start, 'HH:mm')} - {format(eventInfo.event.end, 'HH:mm')}
              </Text>
          )}
          {/* Show Project Name */}
          {task.projectId && eventInfo.view.type.startsWith('timeGrid') && !eventInfo.event.allDay && (
            <HStack spacing={1} mt={1}>
              <Box as={FiFolder} size="10px" color={timeColor} />
              <Text fontSize="xs" color={timeColor} noOfLines={1}>
                {/* TODO: Get actual project name, placeholder for now */}
                {getProjectByIdFn(task.projectId)?.title ?? 'Project'}
              </Text>
            </HStack>
          )}
          {/* Show Notes (First line) */}
          {task.notes && eventInfo.view.type.startsWith('timeGrid') && !eventInfo.event.allDay && (
             <Text fontSize="xs" color={timeColor} mt={0.5} noOfLines={1}>
                {task.notes}
             </Text>
          )}
        </Box>
      </Box>
    );
  }, [handleSelectTask, handleToggleDone, t, getProjectById, format, eventTimeColor]);

  // --- Effect to save additional timezones to localStorage ---
  useEffect(() => {
    try {
      localStorage.setItem('tempo_additional_timezones', JSON.stringify(additionalTimezones));
    } catch (error) {
      console.error("Error saving timezones to localStorage:", error);
    }
  }, [additionalTimezones]);

  // Return the content without the PageLayout wrapper
  // The parent (CalendarLayout) provides the TitleBar and fixed container
  console.log('[Calendar v0] Rendering with selectedDate:', selectedDate);

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] p-4">
        <Spinner size="xl" thickness="4px" color="pink.500" />
      </div>
    );
  }

  // If auth loaded but no user, maybe show a login prompt or different UI?
  // For now, it will proceed and likely show empty state if fetch wasn't called.

  return (
    <>
      {/* Pass props to the TitleBar via CalendarLayout in App.tsx if needed */}
      {/* Example: title={t('v0/calendar:title')} customAction={calendarCustomAction} */}

      {/* Inject the imported styles */}
      <style>{baseCalendarStyles}</style>

      {/* Replace Container with div and Tailwind classes */}
      {/* Apply max-width, centering, and flex properties - REMOVE PADDING HERE */}
      <div
        className="h-full flex flex-col w-full max-w-full mx-auto relative overflow-hidden" // Added overflow-hidden
      >
        {/* // Ensure auth is not loading anymore */}
        {tasksLoadable.state === 'loading' && !authLoading ? (
          // Loading state needs padding if container doesn't have it
          <div className="flex items-center justify-center min-h-[50vh] p-4">
            <div className="flex flex-col items-center space-y-3">
              <Spinner size="xl" thickness="4px" color="pink.500" />
              <Text className={loadingTextColorClass}>{t('v0/calendar:loadingText')}</Text>
            </div>
          </div>
        ) : tasksLoadable.state === 'hasError' ? (
          // Error state needs padding if container doesn't have it
          <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
            <Text className={`${errorTextColorClass} mb-3`}>{t('v0/calendar:loadingError')}</Text>
            <Button onClick={() => fetchTasks()} leftIcon={<FiRefreshCw />}>
              {t('v0/calendar:tryAgain')}
            </Button>
          </div>
        ) : (
          // Grid layout: Mobile (Top/Bottom Split), Desktop (Left/Right Split)
          // Reduce padding and gap for tighter layout
          // Adjust grid based on panel collapse state
          <div className={`grid ${!isDesktop && !isPanelCollapsed && calendarView !== 'listWeek' ? 'grid-rows-[65%_35%]' : 'grid-rows-1'} md:grid-rows-1 ${!isPanelCollapsed && calendarView !== 'listWeek' ? 'md:grid-cols-3' : 'md:grid-cols-1'} gap-0 h-full overflow-hidden py-1 px-1 md:py-2 md:px-1`}>

            {/* Calendar Area (Mobile: Top Row/Full, Desktop: Left Column(s)/Full) */}
            {/* Adjust col-span based on collapse state and view */}
            <div
              ref={calendarWrapperRef} // Add ref to the wrapper
              className={`${calendarView !== 'listWeek' ? (!isPanelCollapsed ? 'md:col-span-2' : 'md:col-span-1') : 'md:col-span-1'} row-span-1 md:row-auto h-full flex flex-col ${!isPanelCollapsed && calendarView !== 'listWeek' ? 'md:pr-3' : ''}`}
            >
              {/* Calendar Wrapper div (styling + overflow control) */}
              <div
                className={`rounded-lg calendar-container flex-grow h-full overflow-hidden`}
                style={{ backgroundColor: bgColor, borderColor: borderColor }}
              >
                 {/* Inner Padding div - Reduce padding */}
                 <div className="p-1 md:p-2 h-full">
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                      initialView={calendarView}
                      views={{
                        dayGridMonth: {
                          titleFormat: { year: 'numeric', month: 'long' },
                          dayMaxEventRows: 4, // Limit number of events shown per day
                        },
                        timeGridWeek: {
                          titleFormat: { year: 'numeric', month: 'long', day: '2-digit' },
                        },
                        timeGridDay: {
                          titleFormat: { year: 'numeric', month: 'long', day: '2-digit' },
                        },
                        listWeek: {
                          titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }, // Show day in list view title
                          duration: { weeks: 1 }, // Show one week list by default
                          noEventsContent: t('v0/calendar:noEventsToday'), // Customized no events text
                        }
                      }}
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'timezoneSettings'
                      }}
                      customButtons={{
                        timezoneSettings: {
                          text: 'Timezones',
                          click: onTimezoneModalOpen,
                        }
                      }}
                      events={events}
                      editable={true}
                      selectable={true}
                      selectMirror={true}
                      dayMaxEvents={true}
                      eventDrop={handleEventDrop}
                      eventDisplay="block" // Important for custom content layout
                      eventContent={(arg) => renderEventContent(arg, getProjectById, eventTimeColor)} // Pass necessary values
                      eventTimeFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }}
                      slotLabelFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      }}
                      allDaySlot={true}
                      firstDay={1}
                      locale={i18n.language}
                      buttonText={{
                        today: t('v0/calendar:buttons.today'),
                        month: t('v0/calendar:buttons.month'),
                        week: t('v0/calendar:buttons.week'),
                        day: t('v0/calendar:buttons.day'),
                        list: t('v0/calendar:buttons.list') // Add translation for list if needed
                      }}
                      dateClick={handleDateClick} // Use the NEW detailed click handler
                      select={handleDateSelect} // Keep using this for DRAG selection
                      datesSet={handleDatesSet} // Keep using existing handler (now also updates activeView)
                      height="100%"
                      selectMinDistance={5} // Require a minimum drag distance to trigger select
                      nowIndicator={true} // Show a horizontal line indicating the current time
                      scrollTime="06:00:00" // Set a fixed initial scrollTime (default is 6am anyway)
                      slotDuration="00:30:00" // 30-minute slots
                      slotLabelInterval="01:00" // Show hour labels
                      // contentHeight="auto" // Explicit height='100%' should handle this
                    />
                 </div>
              </div>
            </div>

            {/* --- Right Sidebar for Desktop --- */}
            {/* Conditionally render based on collapse state and view */}
            {!isPanelCollapsed && calendarView !== 'listWeek' && (
              // Reduce gap for desktop sidebar
              // Added 'hidden md:grid' to ensure it only shows on desktop when not collapsed
              <div className="hidden md:flex md:flex-col md:border-l border-gray-200 dark:border-gray-700 md:pl-3 md:col-span-1 md:h-full md:overflow-hidden">
                {/* Event List (Desktop) - Reduce internal padding? Check EventList.tsx */}
                <div className="flex-1 h-full overflow-y-auto mb-4">
                  <EventList
                    date={selectedDate}
                    events={selectedDateEvents}
                    onToggleDone={handleToggleDone}
                    onDelete={handleDeleteTask}
                    onSelect={handleSelectTask}
                  />
                </div>
                {/* Diary Editor (Desktop) - Reduce internal padding? Check DiaryEditor.tsx */}
                <div className="flex-1 h-full overflow-y-auto pt-4 border-t border-gray-200 dark:border-gray-700">
                  <DiaryEditor date={selectedDate} />
                </div>
              </div>
            )}

            {/* --- Bottom Split Area for Mobile --- */}
            {/* Conditionally render based on collapse state and view */}
            {!isPanelCollapsed && calendarView !== 'listWeek' && (
              // Reduce gap and padding for mobile bottom area
              // Added 'md:hidden' to ensure it only shows on mobile when not collapsed
              <div className="row-span-1 grid grid-cols-2 gap-3 h-full overflow-hidden pt-3 border-t border-gray-200 dark:border-gray-700 mt-1 md:hidden">
                {/* Event List (Mobile) - Reduce padding, rounding, shadow */}
                <div className="col-span-1 h-full overflow-y-auto rounded-lg">
                  <EventList
                     date={selectedDate}
                     events={selectedDateEvents}
                     onToggleDone={handleToggleDone}
                     onDelete={handleDeleteTask}
                     onSelect={handleSelectTask}
                  />
                </div>
                {/* Diary Editor (Mobile) - Reduce padding, rounding, shadow */}
                <div className="col-span-1 h-full overflow-y-auto rounded-lg border-l border-gray-200 dark:border-gray-700 pl-3">
                   <DiaryEditor date={selectedDate} />
                </div>
             </div>
            )}
          </div>
        )}

        {/* Collapse/Expand Toggle Button */}
        {/* Show toggle only if not in listWeek view */}
        {calendarView !== 'listWeek' && (
          <Button
            size="xs"
            position="absolute"
            // Positioning for Mobile (bottom center)
            bottom={isDesktop ? "auto" : (isPanelCollapsed ? "0.75rem" : "calc(35% - 0.5rem)")} // Adjust bottom based on collapsed state for mobile
            left={isDesktop ? "auto" : "50%"}
            // Positioning for Desktop (right center)
            right={isDesktop ? (isPanelCollapsed ? "0.75rem" : "calc(33.33% - 0.5rem)") : "auto"} // Adjust right based on collapsed state for desktop
            top={isDesktop ? "50%" : "auto"}
            transform={isDesktop ? "translateY(-50%)" : "translateX(-50%)"} // Apply transform based on viewport
            zIndex="docked" // Ensure button is above other elements
            onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
            aria-label={isPanelCollapsed ? t('v0/calendar:buttons.expandPanel') : t('v0/calendar:buttons.collapsePanel')}
            variant="solid"
            bg={useColorModeValue('rgba(237, 100, 166, 0.9)', 'rgba(237, 100, 166, 0.8)')}
            color="white"
            _hover={{ bg: useColorModeValue('rgba(237, 100, 166, 1)', 'rgba(237, 100, 166, 0.9)') }}
            rounded="full"
            w="28px"
            h="28px"
            minW="28px"
            boxShadow="0 2px 5px rgba(0,0,0,0.2)"
            border="none"
            p="0"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {isDesktop ? (
              isPanelCollapsed ? <ChevronLeftIcon boxSize={4} /> : <ChevronRightIcon boxSize={4} />
            ) : (
              isPanelCollapsed ? <ChevronUpIcon boxSize={4} /> : <ChevronDownIcon boxSize={4} />
            )}
          </Button>
        )}
      </div>

      {/* Task Form Modal with initialData */}
      <TaskFormModal
        isOpen={isTaskFormOpen}
        onClose={() => {
          setIsTaskFormOpen(false);
          setSelectedTask(null);
          setNewTaskInitialData({});
        }}
        task={selectedTask}
        onSubmit={handleTaskSubmit}
        isSubmitting={isSubmitting}
        initialData={newTaskInitialData}
      />

      {/* Timezone Settings Modal */}
      <TimezoneSettingsModal
        isOpen={isTimezoneModalOpen}
        onClose={onTimezoneModalClose}
        selectedTimezones={additionalTimezones}
        onTimezonesChange={setAdditionalTimezones}
        mainTimezone={calendarRef.current?.getApi().getOption('timeZone') || 'local'}
      />
    </>
  );
};